import { Injectable } from '@angular/core';
import { FilterAssistQuery } from '../../../interfaces/filter-assist-query';
import { ShipmentFilterService } from '../services/shipment-filter.service';
import { ShipmentQuery } from '../../../models/shipment-query';
import { Observable, of } from 'rxjs';
import { FilterAssist } from '../../../models/filter-assist';
import { ShipmentStatusEnum } from '../../../enum/shipment-status.enum';
import { FilterCategoryEnum } from '../../../enum/filter-category.enum';

@Injectable({
    providedIn: 'root'
})
export class ShipmentStatusFilterService implements FilterAssistQuery<ShipmentQuery> {

    private SHIPMENT_STATUS = 'SHIPMENT_STATUS';

    constructor(private shipmentFilterService: ShipmentFilterService) {
        this.shipmentFilterService.registerFilters(this);
    }

    protected description(state: string): string {
        switch (state) {
            case ShipmentStatusEnum.DELIVERED:
                return $localize`Delivered Shipments`;
            case ShipmentStatusEnum.EXCEPTION:
                return $localize`Shipments with Exceptions`;
            case ShipmentStatusEnum.IN_TRANSIT:
                return $localize`Shipments In-Transit`;
            case ShipmentStatusEnum.MANIFESTED:
                return $localize`Shipments Manifested for Shipping`;
            case ShipmentStatusEnum.QUOTE:
                return $localize`Quotes`;
            case ShipmentStatusEnum.QUOTED:
                return $localize`Quoted Shipments`;
            case ShipmentStatusEnum.VOID:
                return $localize`Voided Shipments`;
            case ShipmentStatusEnum.SUBMITTED:
                return $localize`Shipments Submitted to Carrier`;
            default:
                return null;
        }
    }

    active(query: ShipmentQuery): Observable<FilterAssist[]> {
        if (query.state) {
            const description = this.description(query.state);
            if (description) {
                return of([ {
                    id: this.SHIPMENT_STATUS,
                    description,
                    parameter: query.state,
                    score: 50,
                    category: FilterCategoryEnum.STATUS
                } ]);
            }
        }
        return of([]);
    }

    apply(id: string, parameter: any, query: ShipmentQuery): void {
        query.state = parameter;
    }

    ids(): string[] {
        return [ this.SHIPMENT_STATUS ];
    }

    protected matchTerm(terms: string, status: string): number {
        terms = terms.toLowerCase();
        if (status.startsWith(terms)) {
            return 5;
        }
        const description = this.description(status).toLowerCase();
        if (description.startsWith(terms)) {
            return 40;
        }
        if (description.indexOf(terms) !== -1) {
            return 60;
        }
        return 0;
    }

    match(terms: string): Observable<FilterAssist[]> {
        if (terms.length < 3) {
            return of([]);
        }
        terms = terms.toLowerCase();
        return of(Object.keys(ShipmentStatusEnum)
            .filter(state => this.description(state))
            .filter(state => this.matchTerm(terms, state))
            .map(state => ({
                id: this.SHIPMENT_STATUS,
                description: this.description(state),
                parameter: state,
                score: this.matchTerm(terms, state),
                category: FilterCategoryEnum.STATUS
            })));
    }

    unapply(id: string, query: ShipmentQuery): void {
        query.state = null;
    }
}
